import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Calendar, Mail, Phone, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const API = window.location.origin + '/api';

const BookingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      navigate('/');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setChecking(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus(response.data);
        setChecking(false);
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    }
  };

  if (checking) {
    return (
      <div data-testid="payment-checking-screen" className="min-h-screen flex items-center justify-center">
        <Card className="card-feature max-w-md text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-primary" />
          <h2 className="text-2xl font-bold mb-2" style={{fontFamily: 'Fraunces, serif'}}>Confirming Your Payment...</h2>
          <p className="text-muted-foreground">Please wait while we verify your booking.</p>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="booking-success-page" className="min-h-screen flex items-center justify-center px-6 py-12">
      <Card className="card-feature max-w-2xl w-full text-center">
        <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-12 h-12 text-white" />
        </div>
        
        <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Fraunces, serif'}}>Booking Confirmed!</h1>
        <p className="text-lg text-muted-foreground mb-8">
          Your session has been successfully booked and paid for.
        </p>
        
        <div className="bg-muted rounded-xl p-6 mb-8 text-left">
          <h3 className="font-semibold mb-4 text-center">What Happens Next?</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Confirmation Email</p>
                <p className="text-sm text-muted-foreground">Check your inbox for all session details and location info</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Reminder</p>
                <p className="text-sm text-muted-foreground">We'll send you a reminder before your session</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Questions?</p>
                <p className="text-sm text-muted-foreground">Call us anytime at <a href="tel:0472555357" className="text-primary hover:underline">0472 555 357</a></p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button data-testid="return-home-btn" className="btn-primary" onClick={() => navigate('/')}>
            Return to Home
          </Button>
          <Button data-testid="book-another-btn" className="btn-secondary" onClick={() => navigate('/book')}>
            Book Another Session
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default BookingSuccess;
